// Importar tipos de servicios
import type { FunctionalUnit } from '../services/functionalUnitService';

// Tipos base para las respuestas de la API
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
}

// ==================== TRANSACCIONES Y CUENTA CORRIENTE ====================

/**
 * Tipos de cuenta contable (AccountType)
 * Usado internamente para clasificar los movimientos en TransactionEntry
 */
export enum AccountType {
  CAJA = 'CAJA',
  CUENTA_CORRIENTE_CLIENTE = 'CUENTA_CORRIENTE_CLIENTE',
  DEUDORES_POR_VENTAS = 'DEUDORES_POR_VENTAS',
  CUENTA_PROVEEDOR = 'CUENTA_PROVEEDOR',
  CUENTA_TRANSITORIA = 'CUENTA_TRANSITORIA',
  INGRESOS_VARIOS = 'INGRESOS_VARIOS',
  GASTOS_VARIOS = 'GASTOS_VARIOS',
  EXTERNO = 'EXTERNO',
}

/**
 * Tipo de transacción de Cuenta Corriente Cliente
 * Usado en el endpoint POST /customer-current-account-transactions
 */
export enum TransactionTypeCC {
  INGRESO = 'INGRESO',
  EGRESO = 'EGRESO',
  PAGO = 'PAGO',
  CARGO = 'CARGO',
}

/**
 * Payload para crear un egreso de caja (gastos)
 * POST /api/transactions/expense
 */
export interface CreateExpenseRequest {
  cashBoxId: number;
  amount: number;
  description?: string;
}

/**
 * Payload para crear una transferencia interna entre cajas
 * POST /api/transactions/transfer
 */
export interface CreateTransferRequest {
  sourceCashBoxId: number;
  targetCashBoxId: number;
  amount: number;
  /** Tipo de cambio requerido cuando las cajas tienen distinta moneda (CAMBIO_MONEDA). */
  exchangeRate?: number;
  description?: string;
}

/**
 * DTO para el reporte de balance diario
 * GET /api/reports/daily-balance
 */
export interface DailyBalanceDTO {
  date: string; // Formato YYYY-MM-DD
  totalCreditos: number; // Ingresos (EntryType CREDITO = positivo)
  totalDebitos: number; // Egresos (EntryType DEBITO = negativo)
  netChange: number; // Cambio neto (totalCreditos - totalDebitos)
}

/**
 * DTO para el reporte de movimientos detallados de caja
 * GET /api/reports/cashbox-movements
 */
export interface ReportMovementDTO {
  id: number;
  transactionId: number;
  date: string; // YYYY-MM-DD
  description: string;
  transactionType: string;
  entryType: 'CREDITO' | 'DEBITO';
  amount: number;
  currency: string;
}

export interface CashBoxMovementsParams {
  cashBoxId: number;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

/**
 * Parámetros para reportes de balance diario
 * (Legacy / Reutilizable si se mantiene el endpoint anterior)
 */
export interface DailyBalanceParams {
  cashBoxId: number;
  startDate: string; // Formato YYYY-MM-DD
  endDate: string; // Formato YYYY-MM-DD
}

/**
 * Response genérico para operaciones de transacciones
 * que devuelven solo el ID de la transacción creada
 */
export interface TransactionIdResponse {
  success: boolean;
  message: string;
  data: number; // ID de la transacción
  timestamp: string;
}

/**
 * Estado de una transacción
 */
export enum TransactionStatus {
  COMPLETADA = 'COMPLETADA',
  PENDIENTE = 'PENDIENTE',
  CANCELADA = 'CANCELADA',
  REVERTIDA = 'REVERTIDA',
}

/**
 * Tipo de transacción en el nuevo sistema
 */
export enum TransactionType {
  INGRESO = 'INGRESO',
  EGRESO = 'EGRESO',
  TRANSFERENCIA = 'TRANSFERENCIA',
  AJUSTE = 'AJUSTE',
}

/**
 * Entrada contable de una transacción
 * El backend envía entryType (DEBITO/CREDITO) + amount en lugar de debit/credit
 */
export interface TransactionEntry {
  id: number;
  entryType: 'DEBITO' | 'CREDITO';
  accountType: string;
  accountId: number;
  amount: number;
  currency: string;
}

/**
 * Link a otra entidad relacionada con la transacción
 */
export interface TransactionLink {
  id: number;
  linkedEntityType: string; // PAYMENT_INSTALLMENT, SALE, CUSTOMER, etc.
  linkedEntityId: number;
  description: string;
}

/**
 * DTO de respuesta para transacciones (Auditoría)
 * GET /api/transactions
 */
export interface TransactionResponseDTO {
  id: number;
  type: string;
  status: string;
  currency: string;
  executedAt: string; // ISO 8601
  accountingDate: string; // YYYY-MM-DD
  description: string;
  exchangeRate: number | null;
  entries: TransactionEntry[];
  links: TransactionLink[];
}

// Roles de usuario (ya no hardcoded, viene del backend)
export type UserRole = string;

// Permiso individual
export interface Permission {
  id: number;
  code: string;
  module: string;
  description: string;
}

// Rol con permisos
export interface RoleWithPermissions {
  id: number;
  name: string;
  description: string;
  isSystem: boolean;
  isActive: boolean;
  permissions: Permission[];
}

// Usuario
export interface User {
  id: number;
  email: string;
  roleId: number;
  roleName: string;
  name?: string;
  permissions: string[];
  isOwner?: boolean;
}

// Gestión de usuarios (ADMIN)
export interface UserManagement {
  id: number;
  email: string;
  name: string | null;
  roleId: number;
  roleName: string;
  isActive: boolean;
  permissions: string[];
  isOwner: boolean;
  phoneNumber: string | null;
  bio: string | null;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  roleId: number;
  name: string;
}

export interface UpdateUserRequest {
  name?: string;
  roleId?: number;
  isActive?: boolean;
}

export interface UserProfileResponse {
  id: number;
  email: string;
  roleName: string;
  name: string | null;
  phoneNumber: string | null;
  profilePictureUrl: string | null;
  bio: string | null;
  isActive: boolean;
  isOwner: boolean;
}

export interface UpdateUserProfileRequest {
  name?: string;
  phoneNumber?: string;
  profilePictureUrl?: string;
  bio?: string;
}

export interface InvitationTokenResponse {
  id: number;
  email: string;
  tenantId: number;
  roleId: number;
  token: string;
  expiresAt: string;
  createdAt: string;
}

export enum InvitationStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
  SUPERSEDED = 'SUPERSEDED',
  INVALIDATED_BY_REGISTRATION = 'INVALIDATED_BY_REGISTRATION',
}

export interface InvitationAdminResponse {
  id: number;
  email: string;
  status: InvitationStatus;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
  roleId: number;
}

export interface InviteEmployeeRequest {
  email: string;
  roleId: number;
  personalMessage?: string;
}

export interface AcceptInvitationRequest {
  token: string;
  name: string;
  password: string;
  confirmPassword: string;
  otpCode: string;
  phoneNumber?: string;
  profilePictureUrl?: string;
  bio?: string;
}

export interface AcceptInvitationResponse {
  token?: string;
  user?: {
    id: number;
    email: string;
    roleId: number;
    roleName: string;
    name: string;
    permissions: string[];
    tenantId: string;
  };
}

export interface RegisterTenantRequest {
  companyName: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  legalName: string;
  taxId: string;
  industry: string;
  companyEmail?: string;
  companyPhone?: string;
  address: string;
  country: string;
  province: string;
  city: string;
  employeeCount: string;
  adminPhoneNumber?: string;
  adminProfilePictureUrl?: string;
  adminBio?: string;
}

// ── OTP ──────────────────────────────────────────────────────────────────────

export interface RequestOtpRequest {
  email: string;
}

export interface ConfirmRegistrationRequest extends RegisterTenantRequest {
  otpCode: string;
}

export interface ValidateInviteTokenResponse {
  maskedEmail: string;
  tenantName: string;
}

export interface RegisterTenantResponse {
  token?: string;
  user?: {
    id: number;
    email: string;
    roleId: number;
    roleName: string;
    name: string;
    permissions: string[];
    isOwner?: boolean;
    tenantId?: string;
    rbacVersion?: string;
  };
}

export enum EmailStatusType {
  AVAILABLE = 'AVAILABLE',
  USER_ALREADY_EXISTS = 'USER_ALREADY_EXISTS',
  PENDING_INVITATION = 'PENDING_INVITATION',
}

export interface EmailStatusResponse {
  status: EmailStatusType;
  tenantId?: number | null;
  tenantName?: string | null;
  invitationCreatedAt?: string | null;
}

export enum CompanyNameStatusType {
  AVAILABLE = 'AVAILABLE',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
}

export interface CompanyNameStatusResponse {
  status: CompanyNameStatusType;
}

export enum TaxIdStatusType {
  AVAILABLE = 'AVAILABLE',
  INVALID_FORMAT = 'INVALID_FORMAT',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
}

export interface TaxIdStatusResponse {
  status: TaxIdStatusType;
}

export interface CompanyProfile {
  id: number;
  tenantId: number;
  companyName: string;
  legalName: string;
  taxId: string;
  industry: string;
  companyEmail: string | null;
  companyPhone: string | null;
  address: string;
  country: string;
  province: string;
  city: string;
  employeeCount: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface CompanyProfileUpdateRequest {
  companyName: string;
  legalName: string;
  taxId: string;
  industry: string;
  companyEmail?: string | null;
  companyPhone?: string | null;
  address: string;
  country: string;
  province: string;
  city: string;
  employeeCount: string;
}

export interface WorkCategory {
  id: number;
  value: string;
  description?: string;
}

export interface GeneralWorkCategory {
  id: number;
  name: string;
  description?: string;
}

export interface WorkCategoryWithPercentage {
  id: number;
  value: string;
  description: string;
  percentage: number;
}

export interface SavedBuildingTypeData {
  buildingName: string;
  workCategories: WorkCategoryWithPercentage[];
}

export interface BuildingType {
  id: number;
  value: string;
  description: string;
  workCategories: WorkCategoryWithPercentage[];
}

export interface BuildingTypeWithoutWorkTypes {
  id: number;
  value: string;
  description: string;
}

export interface ProjectFormData {
  id: number;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  budget: number;
  workCode?: string;
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  totalM2?: number;
  costPerM2?: number;
  totalEstimatedCost?: number;
  actualCost?: number;
  buildingType?: string;
  projectStatus?: WorkStatus;
  client?: string;
  latitude?: number;
  longitude?: number;
  image?: File | null;
  image2?: File | null;
  image3?: File | null;
  workCategories?: Array<{ id: number; percentage: number }>;
  currency?: string;
  createdAt?: string;
  updatedAt?: string;
}

export enum WorkStatus {
  EN_CONSTRUCCION = 'EN_CONSTRUCCION',
  PLANIFICACION = 'PLANIFICACION',
  FINALIZADO = 'FINALIZADO',
  SUSPENDIDO = 'SUSPENDIDO',
  CANCELADO = 'CANCELADO',
}

export const WorkStatusLabels: Record<WorkStatus, string> = {
  [WorkStatus.EN_CONSTRUCCION]: 'En Construcción',
  [WorkStatus.PLANIFICACION]: 'Planificación',
  [WorkStatus.FINALIZADO]: 'Finalizado',
  [WorkStatus.SUSPENDIDO]: 'Suspendido',
  [WorkStatus.CANCELADO]: 'Cancelado',
};

// DEPRECATED: Usar FunctionalUnit del servicio functionalUnitService
// export interface FunctionalUnitFormData {
//   projectId: number;
//   floor: string;
//   letter: string;
//   type: UnitType;
//   location: LocationType;
//   coveredArea: number;
//   semiCoveredArea: number;
//   storageArea: number;
//   freeArea: number;
//   status?: UnitStatus;
//   notes: string;
//   project: {
//     id: number;
//   };
// }

// Cliente (DEPRECATED: Usar Customer del servicio clientsService)
export interface Client {
  //Eliminar este mas tarde
}

export interface PriceList {
  id: number;
  projectId?: number;
  coveredM2: number;
  freePercentage: number;
  semiCoveredPercentage: number;
  bauleraPercentage: number;
  financedPercentage: number;
  currency: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PriceListFormData {
  projectId?: number;
  coveredM2: number;
  freePercentage: number;
  semiCoveredPercentage: number;
  bauleraPercentage: number;
  financedPercentage: number;
  currency: string;
}

//################################################## ELiminar o editar
//##################################################
//##################################################
//##################################################
//##################################################
//##################################################
//##################################################
//##################################################
//##################################################
//##################################################
//##################################################
//##################################################

// Proveedor
// ==================== PROVEEDORES ====================

/**
 * Interfaz para Proveedor
 */
export interface Supplier {
  id: number;
  cuit: string;
  businessName: string;
  category: string;
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  address: string;
  phone: string;
  email?: string;
  paymentTerms: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Request para crear un proveedor
 * Todos los campos obligatorios según la especificación
 */
export interface CreateSupplierRequest {
  cuit: string;
  businessName: string;
  category: string;
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  address: string;
  phone: string;
  email?: string;
  paymentTerms: string;
  notes?: string;
}

/**
 * Request para actualizar un proveedor
 * Todos los campos son obligatorios ya que el CUIT puede cambiar
 */
export interface UpdateSupplierRequest {
  cuit: string;
  businessName: string;
  category: string;
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  address: string;
  phone: string;
  email?: string;
  paymentTerms: string;
  notes?: string;
}

// Proyecto
export interface Project {
  id: number;
  name: string;
  address?: string;
  type?: string;
  status: string;
  clientId?: number;
  client?: Client;
  description?: string;
  startDate?: string;
  endDate?: string;
  budget?: number;
  // New OBRAS fields
  workCode?: string;
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  mapLocation?: string;
  totalM2?: number;
  costPerM2?: number;
  totalEstimatedCost?: number;
  actualCost?: number;
  workType?: WorkType;
  workStatus?: WorkStatus;
  categories?: ProjectCategory[];
  createdAt: string;
  updatedAt: string;
}

export enum WorkType {
  EDIFICIO = 'EDIFICIO',
  CASA = 'CASA',
  CENTRO_COMERCIAL = 'CENTRO_COMERCIAL',
  OFICINAS = 'OFICINAS',
  INDUSTRIAL = 'INDUSTRIAL',
  RESIDENCIAL = 'RESIDENCIAL',
}

export const WorkTypeLabels: Record<WorkType, string> = {
  [WorkType.EDIFICIO]: 'Edificio',
  [WorkType.CASA]: 'Casa',
  [WorkType.CENTRO_COMERCIAL]: 'Centro Comercial',
  [WorkType.OFICINAS]: 'Oficinas',
  [WorkType.INDUSTRIAL]: 'Industrial',
  [WorkType.RESIDENCIAL]: 'Residencial',
};

// export enum WorkStatus {
//   EN_CONSTRUCCION = 'EN_CONSTRUCCION',
//   PLANIFICACION = 'PLANIFICACION',
//   FINALIZADO = 'FINALIZADO',
//   SUSPENDIDO = 'SUSPENDIDO',
//   CANCELADO = 'CANCELADO',
// }

// export const WorkStatusLabels: Record<WorkStatus, string> = {
//   [WorkStatus.EN_CONSTRUCCION]: 'En Construcción',
//   [WorkStatus.PLANIFICACION]: 'Planificación',
//   [WorkStatus.FINALIZADO]: 'Finalizado',
//   [WorkStatus.SUSPENDIDO]: 'Suspendido',
//   [WorkStatus.CANCELADO]: 'Cancelado',
// };

// Centro de Costo
export interface CostCenter {
  id: number;
  name: string;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Banco
export interface Bank {
  id: number;
  name: string;
  code?: string;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Rubro de Obra //ELIMINAR ESTE DESPUES
export interface WorkItem {
  id: number;
  name: string;
  description?: string;
  projectId: number;
  project?: Project;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkItemConfig {
  id: number;
  name: string;
}

// Rubro General
export interface GeneralItem {
  id: number;
  name: string;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Interfaces para el historial de ventas
export interface SaleCustomer {
  customerId: number;
  cuit: string;
  customerType: 'PERSON' | 'COMPANY';
  customerName: string | null;
  email: string;
  phone: string;
  address: string;
}

export interface SaleFunctionalUnit {
  functionalUnitId: number;
  floor: string;
  letter: string;
  type: string;
  location: string;
  projectName: string;
  priceAtSale?: number;
}

export interface SalePaymentMethod {
  paymentId: number;
  currency: 'ARS' | 'USD';
  paymentType: 'EFECTIVO' | 'FINANCIADO';
  accountingType: 'A' | 'B';
  description: string;
  amount: number;
  totalAmount?: number;
  exchangeRate: number;
  paymentDate?: string;
  iva?: number;
  financingType?: string | null;
  firstInstallmentDate?: string | null;
  installments?: number | null;
  indexationType?: string | null;
  indexBaseDate?: string | null;
  moraRate?: number | null;
  moraFrequency?: string | null;
}

export interface SaleHistory {
  id: number;
  status: 'ACTIVE' | 'CANCELLED';
  deliveryDate: string;
  observations: string;
  currency: string;
  exchangeRate: number;
  total: number;
  createdAt: string;
  updatedAt: string;
  customer: SaleCustomer;
  functionalUnits: SaleFunctionalUnit[];
  paymentMethods: SalePaymentMethod[];
}

// Venta
export interface Sale {
  id: number;
  warnings?: string[];
  saleNumber: string;
  description?: string;
  clientId: number;
  client?: Client;
  projectId: number;
  project?: Project;
  functionalUnitId?: number;
  functionalUnit?: FunctionalUnit;
  unitCode?: string;
  totalAmount: number;
  currencyType?: 'ARS' | 'USD' | 'EUR';
  exchangeRate?: number;
  status: string;
  // New enhanced fields
  saleDate?: string;
  boletoAmount?: number;
  boletoInitialPayment?: number;
  boletoInstallmentsCount?: number;
  boletoInstallmentValue?: number;
  boletoPeriod?: string;
  mainInstallmentsAmount?: number;
  mainInstallmentsCount?: number;
  mainInstallmentValue?: number;
  mainAdjustmentIndex?: string;
  mainPeriod?: string;
  reinforcementAmount?: number;
  reinforcementAdjustmentIndex?: string;
  reinforcementPeriod?: string;
  reinforcementInstallmentsCount?: number;
  reinforcementInstallmentValue?: number;
  baseMonth?: string;
  paymentDueDay?: number;
  installments?: SaleInstallment[];
  createdAt: string;
  updatedAt: string;
}

// Cuota de Venta
export interface SaleInstallment {
  id: number;
  saleId: number;
  sale?: Sale;
  installmentNumber: number;
  description?: string;
  amount: number;
  dueDate?: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  paymentDate?: string;
  notes?: string;
  // New enhanced fields
  installmentType: 'BOLETO' | 'MAIN' | 'REINFORCEMENT';
  adjustmentIndex?: string;
  adjustmentPercentage?: number;
  amountToCollect?: number;
  paidAmount?: number;
  balance?: number;
  createdAt: string;
  updatedAt: string;
}

// Flujo de caja mejorado
export interface Cashflow {
  id: number;
  description: string;
  amount: number;
  type: 'INCOME' | 'EXPENSE';
  date: string;

  // Nuevos campos del formulario mejorado
  costCenterId?: number;
  costCenter?: CostCenter;
  projectId?: number;
  project?: Project;
  supplierId?: number;
  supplier?: Supplier;
  clientId?: number;
  client?: Client;
  cashBox?: 'CASH' | 'BANK' | 'CHECK';
  responsible?: string;
  currencyType?: 'ARS' | 'USD' | 'EUR';
  exchangeRate?: number;
  taxType?: 'WHITE' | 'IVA' | 'IIBB' | 'OTHER';
  workItemId?: number;
  workItem?: WorkItem;
  generalItemId?: number;
  generalItem?: GeneralItem;
  saleId?: string;
  checkNumber?: string;
  issuanceDate?: string;
  credits?: string;
  bankId?: number;
  bank?: Bank;
  cuit?: string;
  fromBankOrEntity?: string;
  creditor?: string;

  createdAt: string;
  updatedAt: string;
}

// Configuración
export interface ConfigItem {
  id: number;
  key: string;
  type: string;
  value: string;
  description?: string;
}

export interface ConstructionItemConfig {
  id: number;
  name: string;
}

// Login
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  user: {
    id: number;
    email: string;
    roleId: number;
    roleName: string;
    name: string;
    permissions: string[];
    isOwner?: boolean;
  };
}

// Formularios
export interface ClientFormData {
  clientType: 'PERSONA' | 'EMPRESA';
  cuit: string;
  name: string;
  lastName?: string;
  businessName?: string;
  companyType?: string;
  legalAddress?: string;
  address?: string;
  phone?: string;
  email?: string;
  companyContactName?: string;
  companyContactPhone?: string;
  companyContactEmail?: string;
}

// export interface ProjectFormData {
//   name: string;
//   address: string;
//   type: string;
//   status: string;
//   clientId: number;
//   description: string;
//   startDate: string;
//   endDate: string;
//   budget: number;
//   workCode?: string;
//   street?: string;
//   streetNumber?: string;
//   city?: string;
//   postalCode?: string;
//   country?: string;
//   mapLocation?: string;
//   totalM2?: number;
//   costPerM2?: number;
//   totalEstimatedCost?: number;
//   actualCost?: number;
//   constructionId: number;
//   workType?: WorkType;
//   workStatus?: WorkStatus;
// }

export interface CashflowFormData {
  // Campos básicos
  date: string;
  costCenterId: string;
  projectId: string;
  cashBox: 'CASH' | 'BANK' | 'CHECK';
  responsible: string;
  currencyType: 'ARS' | 'USD' | 'EUR';
  amount: string;
  exchangeRate: string;
  taxType: 'WHITE' | 'IVA' | 'IIBB' | 'OTHER';
  type: 'INCOME' | 'EXPENSE';
  workItemId: string;
  generalItemId: string;
  description: string;
  saleId: string;

  // Campos de pago/acreedor
  checkNumber: string;
  issuanceDate: string;
  credits: string;
  bankId: string;
  cuit: string;
  fromBankOrEntity: string;
  creditor: string;

  // IDs de entidades relacionadas
  supplierId: string;
  clientId: string;

  // Campos de venta (para el pop-up) - opcionales
  selectedSaleId?: string;
  selectedInstallmentId?: string;
}

export interface SupplierFormData {
  name: string;
  contact: string;
  phone: string;
  email: string;
  cuit: string;
  address: string;
}

export interface Budget {
  id: number;
  project: Project;
  description: string;
  totalAmount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface BudgetFormData {
  projectId: string;
  description: string;
  totalAmount: string;
}

export interface Certification {
  id: number;
  project: Project;
  period: string;
  amountCertified: number;
  date: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CertificationFormData {
  projectId: string;
  period: string;
  amountCertified: string;
  date: string;
}

export interface AccountingEntry {
  id: number;
  project?: Project;
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER' | 'ADJUSTMENT';
  amount: number;
  description: string;
  date: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AccountingFormData {
  projectId: string;
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER' | 'ADJUSTMENT';
  amount: string;
  description: string;
  date: string;
}

export interface Collection {
  id: number;
  client?: Client;
  project: Project;
  amount: number;
  dueDate: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  description: string;
  paymentDate?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CollectionFormData {
  clientId: string;
  projectId: string;
  amount: string;
  dueDate: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  description: string;
  paymentDate?: string;
}

export interface Unit {
  id: number;
  priceListId: number;
  priceList?: PriceList;
  floor: string;
  letter: string;
  unitType:
    | 'TWO_ROOMS'
    | 'THREE_ROOMS'
    | 'FOUR_ROOMS'
    | 'GARAGE'
    | 'MOTORCYCLE_GARAGE';
  location?: string;
  coveredSurface: number;
  semiCoveredSurface: number;
  storageSurface: number;
  openSurface: number;
  totalSurface: number;
  cashPrice: number;
  totalPrice: number;
  downPayment: number;
  monthlyInstallmentsCount: number;
  monthlyInstallmentAmount: number;
  monthlyReinforcementsCount: number;
  monthlyReinforcementAmount: number;
  deliveryPayment: number;
  status: 'AVAILABLE' | 'SOLD';
  createdAt: string;
  updatedAt: string;
}

export interface UnitFormData {
  priceListId: string;
  floor: string;
  letter: string;
  unitType:
    | 'TWO_ROOMS'
    | 'THREE_ROOMS'
    | 'FOUR_ROOMS'
    | 'GARAGE'
    | 'MOTORCYCLE_GARAGE';
  location: string;
  coveredSurface: string;
  semiCoveredSurface: string;
  storageSurface: string;
  openSurface: string;
  totalSurface: string;
  cashPrice: string;
  totalPrice: string;
  downPayment: string;
  monthlyInstallmentsCount: string;
  monthlyInstallmentAmount: string;
  monthlyReinforcementsCount: string;
  monthlyReinforcementAmount: string;
  deliveryPayment: string;
  status: 'AVAILABLE' | 'SOLD';
}

// Project Category Types
export interface ProjectCategory {
  id: number;
  projectId: number;
  project?: Project;
  sequenceNumber: number;
  categoryCode?: string;
  categoryType:
    | 'ALBANILERIA_MDO'
    | 'ALBANILERIA_MATERIALES'
    | 'ESTRUCTURA_HORMIGON'
    | 'ESTRUCTURA_HIERROS'
    | 'ESTRUCTURA_MADERAS'
    | 'JARDINERIA'
    | 'PISCINA'
    | 'OBRA_GENERALES'
    | 'PINTURA_MDO'
    | 'ABERTURAS'
    | 'PISOS_Y_REVESTIMIENTOS'
    | 'AMOBLAMIENTOS'
    | 'ELECTRICIDAD_MATERIALES'
    | 'EXCAVACION'
    | 'GASTOS_MUNICIPALES'
    | 'COCINA_MUEBLES'
    | 'PLOMERIA_SANITARIOS'
    | 'PINTURA_MATERIALES'
    | 'ELECTRICIDAD_MDO'
    | 'PLOMERIA_MATERIALES'
    | 'COCINA_MESADAS'
    | 'YESERIA_MDO'
    | 'COCINA_ARTEFACTOS'
    | 'CALEFACCION'
    | 'PLOMERIA_MDO'
    | 'YESERIA_MATERIALES'
    | 'ZINGUERIA'
    | 'AIRE_ACONDICIONADO'
    | 'PLOMERIA_BOMBAS'
    | 'PLOMERIA_TERMOTANQUES'
    | 'HERRAMIENTAS'
    | 'CERRAJERIA'
    | 'ESTRUCTURA_GENERALES'
    | 'HERRERIA'
    | 'HIGIENE_Y_SEGURIDAD';
  incidencePercentage: number;
  estimatedCost: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectCategoryFormData {
  projectId: string;
  sequenceNumber: string;
  categoryCode: string;
  categoryType:
    | 'ALBANILERIA_MDO'
    | 'ALBANILERIA_MATERIALES'
    | 'ESTRUCTURA_HORMIGON'
    | 'ESTRUCTURA_HIERROS'
    | 'ESTRUCTURA_MADERAS'
    | 'JARDINERIA'
    | 'PISCINA'
    | 'OBRA_GENERALES'
    | 'PINTURA_MDO'
    | 'ABERTURAS'
    | 'PISOS_Y_REVESTIMIENTOS'
    | 'AMOBLAMIENTOS'
    | 'ELECTRICIDAD_MATERIALES'
    | 'EXCAVACION'
    | 'GASTOS_MUNICIPALES'
    | 'COCINA_MUEBLES'
    | 'PLOMERIA_SANITARIOS'
    | 'PINTURA_MATERIALES'
    | 'ELECTRICIDAD_MDO'
    | 'PLOMERIA_MATERIALES'
    | 'COCINA_MESADAS'
    | 'YESERIA_MDO'
    | 'COCINA_ARTEFACTOS'
    | 'CALEFACCION'
    | 'PLOMERIA_MDO'
    | 'YESERIA_MATERIALES'
    | 'ZINGUERIA'
    | 'AIRE_ACONDICIONADO'
    | 'PLOMERIA_BOMBAS'
    | 'PLOMERIA_TERMOTANQUES'
    | 'HERRAMIENTAS'
    | 'CERRAJERIA'
    | 'ESTRUCTURA_GENERALES'
    | 'HERRERIA'
    | 'HIGIENE_Y_SEGURIDAD';
  incidencePercentage: string;
  estimatedCost: string;
}

// DEPRECATED: Usar FunctionalUnit del servicio functionalUnitService
// export interface FunctionalUnit {
//   id: number;
//   project?: Project;
//   projectId?: number;
//   floor: string;
//   letter: string;
//   unitType: UnitType;
//   location: LocationType;
//   coveredSurface: number;
//   semiCoveredSurface: number;
//   storageSurface: number;
//   freeSurface: number;
//   totalSurface: number;
//   status: UnitStatus;
//   notes?: string;
//   createdAt?: string;
//   updatedAt?: string;
// }

export enum UnitStatus {
  AVAILABLE = 'AVAILABLE',
  SOLD = 'SOLD',
  RESERVED = 'RESERVED',
  MAINTENANCE = 'MAINTENANCE',
}

export const UnitStatusLabels: Record<UnitStatus, string> = {
  [UnitStatus.AVAILABLE]: 'DISPONIBLE',
  [UnitStatus.SOLD]: 'VENDIDO',
  [UnitStatus.RESERVED]: 'RESERVADO',
  [UnitStatus.MAINTENANCE]: 'MANTENIMIENTO',
};

// Payment Installments (Cuotas de pago)
export interface PaymentInstallment {
  installmentId: number;
  installmentAmount: number;
  adjustedAmount?: number; // Monto ajustado con indexación
  dueDate: string;
  isPaid: boolean;
  paymentDate: string | null;
  accountingDate: string | null;
  cashRegister: string | null;
  costCenter: string | null;
  perceptions: number | null;
  comments: string | null;
  installmentCreatedAt: string;
  installmentUpdatedAt: string;
  paymentId: number;
  currency: string;
  paymentType: string;
  accountingType: string;
  description: string;
  totalAmount: number;
  exchangeRate: number;
  generalPaymentDate: string;
  iva: number;
  financingType: string | null;
  firstInstallmentDate: string | null;
  totalInstallments: number | null;
  indexationType: string | null;
  indexBaseDate: string | null;
  moraRate?: number | null;
  moraFrequency?: string | null;
  currentMoraPreview?: number | null;
  saleId: number;
  deliveryDate: string;
  saleCurrency: string;
  saleTotal: number;
}

// ==================== FACTURAS DE PROVEEDORES ====================

/**
 * Estado de pago de una factura de proveedor
 */
export type SupplierInvoicePaymentStatus =
  | 'PENDIENTE'
  | 'PARCIALMENTE_PAGADA'
  | 'PAGADA';

/**
 * Request para crear una factura de proveedor
 */
export interface CreateSupplierInvoiceRequest {
  supplierId: number;
  purchaseOrderId?: number;
  invoiceNumber: string;
  invoiceDate: string; // YYYY-MM-DD
  currency: 'ARS' | 'USD';
  subtotal: number;
  ivaTotal: number;
  totalAmount: number;
  fileUrl?: string;
  notes?: string;
}

/**
 * Respuesta de factura de proveedor
 */
export interface SupplierInvoiceResponse {
  id: number;
  supplierId: number;
  supplierName: string;
  purchaseOrderId: number | null;
  purchaseOrderNumber: string | null;
  /** Tipo de contabilidad heredado de la OC ('A' | 'B'); null si la factura no tiene OC. */
  accountingType: string | null;
  invoiceNumber: string;
  invoiceDate: string; // YYYY-MM-DD
  currency: 'ARS' | 'USD';
  subtotal: number;
  ivaTotal: number;
  totalAmount: number;
  amountPaid: number;
  pendingAmount: number;
  paymentStatus: SupplierInvoicePaymentStatus;
  fileUrl: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request para registrar un pago a factura de proveedor
 */
export interface PaySupplierInvoiceRequest {
  cashBoxId: number;
  amount: number;
  description?: string;
}

// Transaction History Types
export * from './transactionHistoryTypes';
